import { FC, useEffect, useMemo } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  keywords?: string[];
}

const SEO: FC<SEOProps> = ({
  title = "MeetMyBot",
  description = "AI-powered chatbot platform",
  image,
  url,
  keywords,
}) => {
  const fullTitle = useMemo(
    () => (title ? `${title} | MeetMyBot` : "MeetMyBot"),
    [title]
  );

  const sanitizeUrl = (path: string): string => {
    if (!path) return "";

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const safePath = path.startsWith("/") ? path : `/${path}`;
    return `${window.location.origin}${safePath}`;
  };

  useEffect(() => {
    document.title = fullTitle;

    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        if (property) {
          meta.setAttribute("property", name);
        } else {
          meta.setAttribute("name", name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    updateMetaTag("description", description);

    if (keywords && keywords?.length > 0) {
      updateMetaTag("keywords", keywords.join(", "));
    }

    updateMetaTag("og:title", fullTitle, true);
    updateMetaTag("og:description", description, true);
    updateMetaTag("og:type", "website", true);

    const imageUrl = image ? sanitizeUrl(image) : null;

    if (imageUrl) {
      updateMetaTag("og:image", imageUrl, true);
      updateMetaTag("twitter:image", imageUrl);
    }

    if (url) {
      const fullUrl = sanitizeUrl(url);
      updateMetaTag("og:url", fullUrl, true);
    }

    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", fullTitle);
    updateMetaTag("twitter:description", description);
  }, [title, description, image, url, keywords, fullTitle]);

  return null;
};

export default SEO;
